import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getActiveProjectMutationKey, getFlatProjectsMutationKey, getProjectUsersMutationKey } from '.';
import { projectService } from '../../application/ProjectService';

import { Project } from '../../domain/project/Project';
import { ProjectUser } from '../../domain/project/ProjectUser';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseRemoveProjectUserProps {
  projectUser: ProjectUser;
}

type UseCreateProjectUserResponseProps = ServiceResponse<({ projectUser }: UseRemoveProjectUserProps) => void, void>;

// Hooks act as 'Adapter' layer.
const useRemoveProjectUser = ({ project }: { project: Project }): UseCreateProjectUserResponseProps => {
  const queryClient = useQueryClient();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ projectUser }: UseRemoveProjectUserProps): Promise<Project> =>
      projectService.deleteUser({
        projectUser,
        project,
      }),
    {
      onSuccess: (project) => {
        queryClient.invalidateQueries(getFlatProjectsMutationKey());
        queryClient.invalidateQueries(getProjectUsersMutationKey());
        queryClient.invalidateQueries(getActiveProjectMutationKey());
      },
      onError: (err: any) => {
        console.info(`Remove ProjectBase User Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ projectUser }: UseRemoveProjectUserProps) => mutate({ projectUser }),
  };
};

export default useRemoveProjectUser;
